import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { Line } from '@ant-design/charts';
import dayjs from 'dayjs';
import {
  getCollectionPointStats,
  updateCollectionPoint,
  deleteCollectionPoint,
} from '../modules/collection-points/api';
import { listSitesForPartner } from '../modules/sites/api';
import { escapeCsvField, downloadCsv } from '../utils/csv';
import LinkedTicketsPanel from '../components/LinkedTicketsPanel';

const numParser = (v) =>
  v == null
    ? ''
    : String(v)
        .replace(/,/g, '.')
        .replace(/[^\d.]/g, '');
const numFormatter = (v) => (v == null || v === '' ? '' : String(v));

const fmtDate = (iso) => (iso ? dayjs(iso).format('DD MMM YYYY') : '—');

/**
 * CT prod readiness (Aug 2026) — the dedicated collection-point ("Caisse")
 * page. Replaces the list's edit modal for existing boxes: identity + edit
 * form, per-box KPIs, the fill-rate series (the OVO-170 tuning signal — a
 * box always at 3/10 is passed too often), and the passage history.
 */
const CollectionPointDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    const { response, error } = await getCollectionPointStats(id);
    if (error) {
      message.error(error.message || 'Failed to load collection point');
      setLoading(false);
      return;
    }
    setData(response);
    const p = response.point;
    form.setFieldsValue({
      siteId: p.siteId ?? p.site?.id,
      name: p.name,
      externalRef: p.externalRef,
      capacityKg: p.capacityKg,
      minKg: p.minKg,
      accessType: p.accessType,
      containerType: p.containerType,
      notes: p.notes,
      isArchived: p.isArchived,
    });
    listSitesForPartner(p.partnerId).then(({ response: sr }) => {
      if (Array.isArray(sr)) setSites(sr);
    });
    setLoading(false);
  }, [id, form]);

  useEffect(() => {
    load();
  }, [load]);

  const point = data?.point;
  const kpis = data?.kpis;

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    // #101 — a moved box takes its recurring passages + future orders with it
    // (routes the driver was already told about stay as planned).
    const relocating = point.siteId != null && values.siteId !== point.siteId;
    if (relocating) {
      const nSchedules = point._count?.schedules ?? 0;
      const ok = await new Promise((resolve) =>
        Modal.confirm({
          title: 'Move this box to another site?',
          content: `${nSchedules} recurring schedule(s) and their future orders will follow the box. Routes already sent to a driver keep the old site; the others are re-timed by the nightly generation.`,
          okText: 'Move',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        }),
      );
      if (!ok) return;
    }
    setSaving(true);
    const { response, error } = await updateCollectionPoint(point.id, {
      siteId: values.siteId,
      name: values.name?.trim(),
      externalRef: values.externalRef?.trim() || null,
      capacityKg: values.capacityKg == null ? null : Number(values.capacityKg),
      minKg: values.minKg == null ? null : Number(values.minKg),
      accessType: values.accessType?.trim() || null,
      containerType: values.containerType?.trim() || null,
      notes: values.notes ?? null,
      isArchived: !!values.isArchived,
    });
    setSaving(false);
    if (error) {
      message.error(error.message || 'Save failed');
      return;
    }
    if (response?.relocation) {
      const r = response.relocation;
      message.success(
        `Box moved — ${r.schedules} schedule(s) and ${r.orders} future order(s) follow it` +
          (r.ordersLeftOnLockedRoutes
            ? `; ${r.ordersLeftOnLockedRoutes} order(s) on dispatched routes left unchanged`
            : ''),
        8,
      );
    } else {
      message.success('Collection point updated');
    }
    load();
  };

  const handleDelete = async () => {
    const { error } = await deleteCollectionPoint(point.id);
    if (error) {
      message.error(error.message || 'Delete failed');
      return;
    }
    message.success('Collection point deleted');
    navigate('/admin/collection-points');
  };

  const exportCsv = () => {
    const lines = [
      ['Date', 'Order', 'Route', 'Driver', 'Fill (/10)', 'Weight (kg)', 'Status'].map(escapeCsvField),
      ...(data?.passages || []).map((p) =>
        [
          p.date ? dayjs(p.date).format('YYYY-MM-DD') : '',
          p.orderCode || '',
          p.routeId ?? '',
          p.driverName || '',
          p.fillTenths ?? '',
          p.weight ?? '',
          p.status || '',
        ].map(escapeCsvField),
      ),
    ];
    downloadCsv(lines, `collection-point-${point.externalRef || point.id}-passages.csv`);
  };

  const chartData = useMemo(
    () =>
      (data?.series || [])
        .filter((s) => s.fillTenths != null)
        .map((s) => ({ date: dayjs(s.date).format('YYYY-MM-DD'), fill: s.fillTenths })),
    [data?.series],
  );

  const passageColumns = [
    { title: 'Date', dataIndex: 'date', key: 'date', width: 120, render: fmtDate },
    {
      title: 'Order',
      key: 'order',
      width: 130,
      render: (_, r) => (r.orderCode ? <Link to={`/admin/orders/${r.orderCode}`}>{r.orderCode}</Link> : '—'),
    },
    {
      title: 'Route',
      key: 'route',
      width: 90,
      render: (_, r) => (r.routeId ? <Link to={`/admin/routes/${r.routeId}`}>#{r.routeId}</Link> : '—'),
    },
    { title: 'Driver', dataIndex: 'driverName', key: 'driver', render: (v) => v || '—' },
    {
      title: 'Fill',
      dataIndex: 'fillTenths',
      key: 'fill',
      width: 90,
      render: (v) => (v != null ? <Tag color={v >= 8 ? 'red' : v >= 5 ? 'orange' : 'green'}>{v}/10</Tag> : '—'),
    },
    {
      title: 'Weight',
      dataIndex: 'weight',
      key: 'weight',
      width: 100,
      render: (v) => (v != null ? `${v} kg` : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v) => (v ? <span className={`status order ${v}`}>{v}</span> : '—'),
    },
  ];

  if (loading && !data) {
    return (
      <div className="Content" style={{ textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!point) {
    return (
      <div className="Content">
        <Alert type="error" showIcon message="Collection point not found" />
      </div>
    );
  }

  const kpi = (label, value, suffix = '') => (
    <Col xs={12} sm={8} md={6} lg={3}>
      <Card size="small">
        <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 600 }}>
          {value ?? '—'}
          {value != null && suffix ? <span style={{ fontSize: 13, fontWeight: 400 }}> {suffix}</span> : null}
        </div>
      </Card>
    </Col>
  );

  return (
    <div className="CollectionPointDetail Content" style={{ paddingBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
        <h2 style={{ margin: 0 }}>
          {point.externalRef ? <Tag color="geekblue">{point.externalRef}</Tag> : null}
          {point.name}
          {point.isArchived ? <Tag style={{ marginLeft: 8 }}>archived</Tag> : null}
        </h2>
        <div style={{ color: 'var(--text-muted)' }}>
          {point.site ? (
            <>
              <Link to={`/admin/partners/${point.partnerId}/sites?focusSiteId=${point.site.id}`}>
                {point.site.name}
              </Link>
              {point.site.commune ? <Tag style={{ marginLeft: 8 }}>{point.site.commune}</Tag> : null}
              <span style={{ marginLeft: 8 }}>{point.site.address}</span>
            </>
          ) : null}
        </div>
        <div style={{ flex: 1 }} />
        <Button onClick={exportCsv}>Export CSV</Button>
      </div>

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        {kpi('Last passage', kpis?.lastPassageAt ? fmtDate(kpis.lastPassageAt) : null)}
        {kpi('Last fill', kpis?.lastFillTenths != null ? `${kpis.lastFillTenths}/10` : null)}
        {kpi('Avg fill', kpis?.avgFillTenths != null ? `${kpis.avgFillTenths}/10` : null)}
        {kpi('Passages (30d)', kpis?.passages30d)}
        {kpi('Passages (total)', kpis?.passagesTotal)}
        {kpi('Kg (30d)', kpis?.kg30d, 'kg')}
        {kpi('Kg (total)', kpis?.kgTotal, 'kg')}
        {kpi('Avg kg / passage', kpis?.avgKgPerPassage, 'kg')}
      </Row>

      <Row gutter={[12, 12]}>
        <Col xs={24} lg={14}>
          <Card size="small" title="Fill rate per passage (/10)" style={{ marginBottom: 12 }}>
            {chartData.length ? (
              <Line
                data={chartData}
                xField="date"
                yField="fill"
                height={220}
                point={{ size: 4 }}
                yAxis={{ min: 0, max: 10 }}
              />
            ) : (
              <Alert
                type="info"
                showIcon
                message="No fill captures yet — they appear once drivers record the 0–10 estimate at pickup."
              />
            )}
          </Card>
          <Card size="small" title={`Passage history (${data.passages.length})`}>
            <Table
              rowKey="orderId"
              size="small"
              columns={passageColumns}
              dataSource={data.passages}
              pagination={{ pageSize: 20, showSizeChanger: false }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          {/* #132 — tickets about this box (incl. driver skips on its orders) */}
          <LinkedTicketsPanel
            filter={{ collectionPointId: point.id }}
            prefill={{
              collectionPointId: point.id,
              collectionPointLabel: point.externalRef ? `${point.name} (${point.externalRef})` : point.name,
              partnerId: point.partnerId,
            }}
            style={{ marginBottom: 12, background: 'var(--surface, #fff)' }}
          />
          <Card
            size="small"
            title="Edit"
            extra={
              <Popconfirm
                title="Delete this collection point? History links survive (SetNull)."
                okButtonProps={{ danger: true }}
                onConfirm={handleDelete}
              >
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            }
          >
            <Form form={form} layout="vertical">
              <Form.Item name="siteId" label="Site" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={sites.map((s) => ({ value: s.id, label: `${s.name} — ${s.address}` }))}
                />
              </Form.Item>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="externalRef" label="Box number">
                    <Input placeholder="e.g. 273" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="capacityKg" label="Capacity (kg)">
                    <InputNumber style={{ width: '100%' }} min={0} parser={numParser} formatter={numFormatter} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="minKg" label="Min (kg)">
                    <InputNumber style={{ width: '100%' }} min={0} parser={numParser} formatter={numFormatter} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="accessType" label="Access (badge / code / clé)">
                    <Input placeholder="e.g. Badge" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="containerType" label="Container">
                    <Input placeholder="e.g. KLEIN" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="notes" label="Notes / access instructions">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="isArchived" label="Archived" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                Save
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CollectionPointDetail;
